'use client'

import { memo } from 'react'
import { useReducedMotion } from 'framer-motion'
import { cn } from '@/lib/utils'
import { MarketingLottie } from './marketing-lottie'

export type MarketingCardMediaKind =
  | 'chat'
  | 'voice'
  | 'desk'
  | 'tray'
  | 'privacy'
  | 'mobile'
  | 'paste'
  | 'crew'

export type MarketingCardMediaProps = {
  kind: MarketingCardMediaKind
  active?: boolean
  className?: string
}

const LOTTIE_BY_KIND: Partial<Record<MarketingCardMediaKind, string>> = {
  chat: '/jsons/ninx_ai_chat_lottie.json',
  voice: '/jsons/audio_lines_animation.json',
  mobile: '/jsons/chat_ai_animation.json',
}

/**
 * baby: Expand-card media: product Lottie when we have it, CSS scenes otherwise.
 * No Ninjapay / Webflow Lottie files.
 */
export const MarketingCardMedia = memo(function MarketingCardMedia({
  kind,
  active = true,
  className,
}: MarketingCardMediaProps) {
  const reduced = Boolean(useReducedMotion())
  const lottieSrc = LOTTIE_BY_KIND[kind]

  if (lottieSrc) {
    return (
      <MarketingLottie
        src={lottieSrc}
        label={`${kind} animation`}
        active={active && !reduced}
        className={className}
      />
    )
  }

  return (
    <div
      className={cn(
        'marketing-card-scene',
        `marketing-card-scene-${kind}`,
        active && !reduced && 'is-live',
        className,
      )}
      aria-hidden
    >
      {kind === 'desk' ? (
        <>
          <span className="marketing-card-scene-window" />
          <span className="marketing-card-scene-bar" />
          <span className="marketing-card-scene-cursor" />
        </>
      ) : null}
      {kind === 'tray' ? (
        <>
          <span className="marketing-card-scene-pill" />
          <span className="marketing-card-scene-dot" />
        </>
      ) : null}
      {kind === 'privacy' ? (
        <>
          <span className="marketing-card-scene-shield" />
          <span className="marketing-card-scene-lock" />
        </>
      ) : null}
      {kind === 'paste' ? (
        <>
          <span className="marketing-card-scene-field" />
          <span className="marketing-card-scene-caret" />
        </>
      ) : null}
      {kind === 'crew' ? (
        <>
          <span className="marketing-card-scene-node a" />
          <span className="marketing-card-scene-node b" />
          <span className="marketing-card-scene-node c" />
          <span className="marketing-card-scene-link" />
        </>
      ) : null}
    </div>
  )
})

/** baby: Map expand-card ids → media kinds for each landing */
export const NINXL_CARD_MEDIA: Record<string, MarketingCardMediaKind> = {
  'local-agent': 'chat',
  inkflow: 'voice',
  crews: 'crew',
}

export const NINK_CARD_MEDIA: Record<string, MarketingCardMediaKind> = {
  dictate: 'voice',
  paste: 'paste',
  local: 'privacy',
}

export const NIN_CARD_MEDIA: Record<string, MarketingCardMediaKind> = {
  ninxl: 'desk',
  nink: 'tray',
  ningenie: 'mobile',
}
